import React, { useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { FaExternalLinkAlt } from "react-icons/fa";

import UserService from "../services/user.service";
import EventBus from "../common/EventBus";
import BoardMyData from "./C2MyData";

// =============================================================================
// AccountPage – "My User Account" (Folgeprompt 15)
// -----------------------------------------------------------------------------
// Stufenuebergreifende Konto-Seite:
//   a) Gut sichtbarer Link/Button "Zum Login auf qr.myn.club" (echtes SSO).
//   b) Passwort aendern (OWASP: aktuelles PW erforderlich, Staerkepruefung).
//   c) Stammdaten/Kontaktdaten – aus dem auth-State angezeigt. Die E-Mail ist
//      zugleich der Login-Schluessel und wird daher hier NICHT editierbar
//      gemacht (Schutz der Anmeldung); Aenderung erfolgt kontrolliert ueber die
//      jeweilige Stufen-/Stammdatenverwaltung.
// =============================================================================

// OWASP-orientierte Passwort-Staerkepruefung (spiegelt Server-Logik).
function validatePassword(pw) {
  if (!pw || !pw.trim()) return "Neues Passwort ist erforderlich.";
  if (pw.length < 8) return "Das Passwort muss mindestens 8 Zeichen lang sein.";
  if (!/[A-Z]/.test(pw)) return "Das Passwort muss mindestens einen Großbuchstaben enthalten.";
  if (!/[a-z]/.test(pw)) return "Das Passwort muss mindestens einen Kleinbuchstaben enthalten.";
  if (!/[0-9]/.test(pw)) return "Das Passwort muss mindestens eine Ziffer enthalten.";
  if (!/[!@#$%^&*(),.?":{}|<>_\-]/.test(pw)) return "Das Passwort muss mindestens ein Sonderzeichen enthalten.";
  return null;
}

const AccountPage = () => {
  const { user: currentUser } = useSelector((state) => state.auth);
  const [searchParams, setSearchParams] = useSearchParams();

  const [form, setForm] = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });
  const [fieldErrors, setFieldErrors] = useState({});
  const [pwLoading, setPwLoading] = useState(false);
  const [pwSuccess, setPwSuccess] = useState(null);
  const [pwError, setPwError] = useState(null);

  const [ssoLoading, setSsoLoading] = useState(false);
  const [ssoError, setSsoError] = useState(null);

  if (!currentUser) {
    return <Navigate to="/login" />;
  }

  const isCustomer = (currentUser.roles || []).includes("ROLE_CUSTOMER");
  const accountTab = isCustomer && searchParams.get("tab") === "daten" ? "daten" : "konto";

  const openTab = (tab) => {
    const next = new URLSearchParams(searchParams);
    if (tab === "daten") next.set("tab", "daten");
    else next.delete("tab");
    setSearchParams(next, { replace: true });
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    const errors = {};
    if (!form.currentPassword) errors.currentPassword = "Bitte geben Sie Ihr aktuelles Passwort ein.";
    const strength = validatePassword(form.newPassword);
    if (strength) errors.newPassword = strength;
    if (!form.confirmPassword) {
      errors.confirmPassword = "Bitte bestätigen Sie das neue Passwort.";
    } else if (form.confirmPassword !== form.newPassword) {
      errors.confirmPassword = "Die Passwörter stimmen nicht überein.";
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handlePasswordSubmit = (event) => {
    event.preventDefault();
    setPwSuccess(null);
    setPwError(null);
    if (!validateForm()) return;

    setPwLoading(true);
    UserService.changeMyAccountPassword({
      currentPassword: form.currentPassword,
      newPassword: form.newPassword,
      confirmPassword: form.confirmPassword,
    })
      .then((response) => {
        setPwSuccess((response.data && response.data.message) || "Passwort erfolgreich geändert.");
        setForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
        setFieldErrors({});
      })
      .catch((error) => {
        if (error.response && error.response.status === 401 && !error.response.data) {
          EventBus.dispatch("logout");
          return;
        }
        const msg =
          (error.response && error.response.data && error.response.data.message) ||
          "Das Passwort konnte nicht geändert werden.";
        setPwError(msg);
      })
      .finally(() => setPwLoading(false));
  };

  const handleQrSso = () => {
    setSsoError(null);
    setSsoLoading(true);
    UserService.issueQrSsoToken()
      .then((response) => {
        const ssoUrl = response.data && response.data.ssoUrl;
        if (ssoUrl) {
          window.location.href = ssoUrl;
        } else {
          setSsoError("SSO-Link konnte nicht erstellt werden.");
        }
      })
      .catch((error) => {
        const msg =
          (error.response && error.response.data && error.response.data.message) ||
          "SSO zu qr.myn.club ist derzeit nicht möglich.";
        setSsoError(msg);
      })
      .finally(() => setSsoLoading(false));
  };

  return (
    <div className="myn-account">
      <header className="myn-account__head">
        <span className="myn-eyebrow">Mein Konto</span>
        <h1 className="myn-account__title">My User Account</h1>
      </header>

      {isCustomer && (
        <div className="myn-sheet__tabs" role="tablist" aria-label="Mein Konto">
          <button type="button" role="tab" aria-selected={accountTab === "konto"} className={accountTab === "konto" ? "myn-sheet__tab is-active" : "myn-sheet__tab"} onClick={() => openTab("konto")}>Konto</button>
          <button type="button" role="tab" aria-selected={accountTab === "daten"} className={accountTab === "daten" ? "myn-sheet__tab is-active" : "myn-sheet__tab"} onClick={() => openTab("daten")}>Meine Daten</button>
        </div>
      )}

      {accountTab === "daten" && <BoardMyData embedded />}

      {accountTab === "konto" && (
      <>
      {/* a) qr.myn.club SSO */}
      <section className="myn-card myn-account__section">
        <h2>Zugang zu qr.myn.club</h2>
        <p className="myn-text-muted">
          Wechseln Sie ohne erneute Anmeldung zu qr.myn.club. Der Zugang wird über ein
          kurzlebiges, sicheres Einmal-Token hergestellt.
        </p>
        {ssoError && (
          <div className="myn-alert myn-alert--danger" role="alert">
            {ssoError}
          </div>
        )}
        <button
          type="button"
          className="myn-btn myn-btn--primary"
          onClick={handleQrSso}
          disabled={ssoLoading}
        >
          {ssoLoading && <span className="spinner-border spinner-border-sm" />}
          <span>Zum Login auf qr.myn.club</span>
          <FaExternalLinkAlt aria-hidden="true" style={{ marginLeft: 8 }} />
        </button>
      </section>

      {/* b) Passwort aendern */}
      <section className="myn-card myn-account__section">
        <h2>Passwort ändern</h2>
        <p className="myn-text-muted">
          Aus Sicherheitsgründen ist Ihr aktuelles Passwort erforderlich. Das neue Passwort muss
          mindestens 8 Zeichen lang sein und Groß-/Kleinbuchstaben, eine Ziffer und ein
          Sonderzeichen enthalten.
        </p>

        {pwSuccess && (
          <div className="myn-alert myn-alert--success" role="status">
            {pwSuccess}
          </div>
        )}
        {pwError && (
          <div className="myn-alert myn-alert--danger" role="alert">
            {pwError}
          </div>
        )}

        <form onSubmit={handlePasswordSubmit} noValidate>
          <div className="myn-field">
            <label className="myn-label" htmlFor="currentPassword">Aktuelles Passwort</label>
            <input
              id="currentPassword"
              name="currentPassword"
              type="password"
              className="myn-input"
              autoComplete="current-password"
              value={form.currentPassword}
              onChange={handleChange}
            />
            {fieldErrors.currentPassword && <div className="myn-error">{fieldErrors.currentPassword}</div>}
          </div>

          <div className="myn-field">
            <label className="myn-label" htmlFor="newPassword">Neues Passwort</label>
            <input
              id="newPassword"
              name="newPassword"
              type="password"
              className="myn-input"
              autoComplete="new-password"
              value={form.newPassword}
              onChange={handleChange}
            />
            {fieldErrors.newPassword && <div className="myn-error">{fieldErrors.newPassword}</div>}
          </div>

          <div className="myn-field">
            <label className="myn-label" htmlFor="confirmPassword">Neues Passwort bestätigen</label>
            <input
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              className="myn-input"
              autoComplete="new-password"
              value={form.confirmPassword}
              onChange={handleChange}
            />
            {fieldErrors.confirmPassword && <div className="myn-error">{fieldErrors.confirmPassword}</div>}
          </div>

          <button type="submit" className="myn-btn myn-btn--primary" disabled={pwLoading}>
            {pwLoading && <span className="spinner-border spinner-border-sm" />}
            <span>Passwort ändern</span>
          </button>
        </form>
      </section>

      {/* c) Stammdaten / Kontaktdaten (Anzeige) */}
      <section className="myn-card myn-account__section">
        <h2>Stammdaten</h2>
        <p className="myn-text-muted">
          Ihre Anmelde- und Kontaktdaten. Die E-Mail-Adresse dient zugleich als Anmeldename und
          kann hier aus Sicherheitsgründen nicht geändert werden.
        </p>
        <dl className="myn-account__data">
          <div>
            <dt>Benutzername</dt>
            <dd>{currentUser.username}</dd>
          </div>
          <div>
            <dt>E-Mail</dt>
            <dd>{currentUser.email || "—"}</dd>
          </div>
          <div>
            <dt>Rollen</dt>
            <dd>{(currentUser.roles || []).map((r) => r.replace(/^ROLE_/, "")).join(", ") || "—"}</dd>
          </div>
        </dl>
      </section>
      </>
      )}
    </div>
  );
};

export default AccountPage;
